/**
 * Converts a CSS color into the 6 digit hex format (without '#') required by docx.
 * docx throws on any other format, so invalid colors are replaced with the fallback.
 *
 * @param {string | null | undefined} color - The color to convert, e.g. '#20add0' or '#fff'.
 * @param {string} fallback - The color to use when the given color is invalid.
 * @return {string} The color as a 6 digit hex string.
 * @notExported
 */
export const toDocxColor = (color: string | null | undefined, fallback: string): string => {
  const hex = (color ?? '').trim().replace(/^#/, '')

  if (/^[0-9a-f]{6}$/i.test(hex)) {
    return hex
  }
  if (/^[0-9a-f]{3}$/i.test(hex)) {
    return hex
      .split('')
      .map(char => char + char)
      .join('')
  }

  return fallback.replace(/^#/, '')
}

/**
 * Recursively replaces null values with undefined in arrays and plain objects.
 * docx crashes when a TextRun gets null as its text, and database values are often null.
 * Other objects, such as dates, are kept as they are.
 *
 * @param {T} value - The value to sanitize.
 * @return {T} A copy of the value without null values.
 * @notExported
 */
const isPlainObject = (value: object): boolean => {
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

export const nullsToUndefined = <T>(value: T): T => {
  if (value === null) {
    return undefined as T
  }
  if (Array.isArray(value)) {
    return value.map(item => nullsToUndefined(item)) as T
  }
  if (typeof value === 'object' && value !== undefined && isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [key, nullsToUndefined(item)])
    ) as T
  }

  return value
}
